import { makeValuesPredicate } from '@sgme/fp';
import type { Action } from 'state/actions';
import type { DataById } from 'state/formData/formData.model';
import type { FormStatus } from 'state/forms/models';
import {
  addKey,
  addOrReplaceKey,
  patchKey,
  patchKeysByPredicate,
  removeIdenticalKeyValues,
  removeKey,
  renameKey,
} from 'state/utils/collection';

import { getOrdersBasketFormReadyStatus, isOrdersBasketFormSelectableByStatus } from './helpers';
import type { OrdersBasketForm, OrdersBasketFormsState } from './models';
import { initialOrdersBasketFormsState } from './models';

const shouldGetStatusFromResult = makeValuesPredicate([
  'READY',
  'NOT_READY',
  'UNKNOWN_ERROR',
  'REJECTED',
] satisfies FormStatus[]);

export function ordersBasketFormsReducer(
  state: OrdersBasketFormsState = initialOrdersBasketFormsState,
  action: Action,
): OrdersBasketFormsState {
  switch (action.type) {
    case 'NEW_ORDERS_BASKET_FORM_REQUESTED':
      return addKey(state, action.temporaryNegotiationId, {
        negotiationId: action.temporaryNegotiationId,
        isSelected: false,
        version: -1,
        status: 'INITIALIZING' as const,
        wrapper: action.wrapper,
        productType: action.productType,
        productSubType: action.productSubType,
      } as Partial<OrdersBasketForm>);
    case 'ORDERS_BASKET_BATCH_WORKSPACE_FORM_INITIALIZED':
      return renameKey(
        patchKey(state, action.temporaryNegotiationId, () => ({
          negotiationId: action.form.negotiationId,
          result: action.form,
          version: action.form.version,
          status: getOrdersBasketFormReadyStatus(action.form),
          productType: action.form.values.ProductType as unknown as string,
          productSubType: action.form.values.ProductSubtype as unknown as string,
          wrapper: action.form.values.Wrapper as unknown as string,
        })),
        action.temporaryNegotiationId,
        action.form.negotiationId,
      );
    case 'ORDERS_BASKET_BATCH_WORKSPACE_FORM_INITIALIZATION_FAILED':
      return patchKey(state, action.temporaryNegotiationId, () => ({ status: 'INITIALIZATION_ERROR' as const }));
    case 'ORDERS_BASKET_FORM_SELECTION_TOGGLED':
      return patchKey(state, action.formId, previousState => ({ isSelected: !previousState.isSelected }));
    case 'ORDERS_BASKET_ALL_FORMS_SELECTED':
      return patchKeysByPredicate(
        state,
        previousState =>
          previousState.productSubType === action.productVariation.productSubType &&
          previousState.productType === action.productVariation.productType &&
          isOrdersBasketFormSelectableByStatus(previousState.status),
        () => ({ isSelected: true }),
      );
    case 'ORDERS_BASKET_ALL_FORMS_UNSELECTED':
      return patchKeysByPredicate(
        state,
        previousState =>
          previousState.productSubType === action.productVariation.productSubType &&
          previousState.productType === action.productVariation.productType &&
          isOrdersBasketFormSelectableByStatus(previousState.status),
        () => ({ isSelected: false }),
      );
    case 'ORDERS_BASKET_FORM_DELETION_REQUESTED':
      return patchKey(state, action.formId, () => ({ status: 'DELETING' as const }));
    case 'ORDERS_BASKET_FORM_DELETED':
      return removeKey(state, action.formId);
    case 'ORDERS_BASKET_FORM_VALUE_CHANGED':
      return patchKey(state, action.formId, previousState => ({
        status: previousState.status !== 'PATCHING' ? ('NOT_READY' as const) : previousState.status,
        dirtyParameters: addOrReplaceKey(previousState.dirtyParameters ?? {}, action.fieldId, action.value) as DataById,
      }));
    case 'ORDERS_BASKET_FORM_UPDATE_REQUESTED':
      return patchKey(state, action.formId, previousState => ({
        status: 'PATCHING' as const,
        updatingParameters: previousState.dirtyParameters,
      }));
    case 'ORDERS_BASKET_FORM_UPDATED':
      return patchKey(state, action.formId, previousState => ({
        status: getOrdersBasketFormReadyStatus(action.result),
        result: action.result,
        version: action.result.version,
        dirtyParameters:
          previousState.dirtyParameters !== undefined
            ? removeIdenticalKeyValues(previousState.dirtyParameters, action.updatedParameters)
            : undefined,
        updatingParameters: undefined,
      }));
    case 'ORDERS_BASKET_FORM_UPDATE_FAILED':
      return patchKey(state, action.formId, () => ({ status: 'PATCH_ERROR' as const, updatingParameters: undefined }));
    case 'ORDERS_BASKET_FORM_QUOTE_REQUESTED':
      return patchKey(state, action.formId, () => ({
        status: 'QUOTE_REQUESTED' as const,
        quoteResponse: undefined,
        quoteAcknowledgement: undefined,
        tradeAuthorization: undefined,
      }));
    case 'ORDERS_BASKET_FORM_QUOTE_CANCELED':
      return patchKey(state, action.formId, () => ({
        status: 'READY' as const,
        quoteResponse: undefined,
        quoteAcknowledgement: undefined,
        tradeAuthorization: undefined,
      }));
    case 'ORDERS_BASKET_FORM_QUOTE_ACCEPTED':
      return patchKey(state, action.formId, () => ({
        status: 'QUOTED_PENDING_AUTHORIZATION' as const,
        quoteAcknowledgement: action.quoteAck,
        version: action.quoteAck.newVersion,
      }));
    case 'ORDERS_BASKET_FORM_QUOTED':
      return patchKey(state, action.formId, () => ({
        status: action.isPendingTradeAuth ? ('QUOTED_PENDING_AUTHORIZATION' as const) : state[action.formId].status,
        version: action.quoteResponse.version,
        quoteResponse: action.quoteResponse,
      }));
    case 'ORDERS_BASKET_FORM_QUOTE_REJECTED':
      return patchKey(state, action.formId, () => ({
        status: 'REJECTED' as const,
        quoteResponse: action.quoteResponse,
      }));
    case 'ORDERS_BASKET_FORM_QUOTE_FAILED':
      return patchKey(state, action.formId, previousState => ({
        status: 'UNKNOWN_ERROR' as const,
        version: action.newVersion ?? previousState.version,
      }));
    case 'ORDERS_BASKET_FORM_TRADE_AUTH_TIMEOUT':
      return patchKey(state, action.formId, () => ({
        status: 'TRADE_AUTHORIZATION_TIMEOUT' as const,
      }));
    case 'ORDERS_BASKET_FORM_QUOTE_TIMEOUT':
      return patchKey(state, action.formId, () => ({
        status: 'QUOTE_TIMEOUT' as const,
      }));

    case 'ORDERS_BASKET_FORM_TRADE_AUTH_FAILED':
    case 'BOOKING_ACK_FAILED':
      return patchKey(state, action.formId, () => ({
        status: 'UNKNOWN_ERROR' as const,
      }));
    case 'ORDERS_BASKET_FORM_TRADE_AUTH_GRANTED':
      return patchKey(state, action.formId, () => ({
        status: action.isPendingQuote ? ('AUTHORIZED_PENDING_QUOTE' as const) : state[action.formId].status,
        tradeAuthorization: action.tradeAuthorization,
      }));
    case 'ORDERS_BASKET_FORM_TRADE_AUTH_REJECTED':
      return patchKey(state, action.formId, () => ({
        status: 'REJECTED' as const,
        tradeAuthorization: action.tradeAuthorization,
      }));
    case 'ORDERS_BASKET_FORM_TRADE_REQUESTED':
      return patchKey(state, action.formId, () => ({ status: 'EXECUTING' as const, tradeResponse: undefined }));
    case 'ORDERS_BASKET_FORM_TRADED':
      return patchKey(state, action.formId, () => ({
        status: action.isBooking ? state[action.formId].status : ('EXECUTED' as const),
        tradeResponse: action.tradeResponse,
      }));
    case 'ORDERS_BASKET_FORM_TRADE_FAILED':
      return patchKey(state, action.formId, () => ({ status: 'UNKNOWN_ERROR' as const }));
    case 'ORDERS_BASKET_FORM_TRADE_REJECTED':
      return patchKey(state, action.formId, () => ({
        status: 'REJECTED' as const,
        tradeResponse: action.tradeResponse,
      }));
    case 'ORDERS_BASKET_FORM_TRADE_TIMEOUT':
      return patchKey(state, action.formId, () => ({
        status: 'TRADE_TIMEOUT' as const,
      }));
    case 'ORDERS_BASKET_FORM_TRADED_WITH_WARNING':
      return patchKey(state, action.formId, () => ({
        status: 'EXECUTED_WITH_WARNING' as const,
        tradeResponse: action.tradeResponse,
      }));
    case 'ORDERS_BASKET_PERSISTED_FORM_FETCH_REQUESTED':
      return addOrReplaceKey(state, action.formId, {
        negotiationId: action.formId,
        isSelected: false,
        version: -1,
        status: 'RELOADING' as const,
        wrapper: action.wrapper,
        productType: action.productType,
        productSubType: action.productSubType,
      });
    case 'ORDERS_BASKET_PERSISTED_FORM_FETCHED':
      return patchKey(state, action.formId, () => ({
        result: action.form.result,
        version: action.form.result.version,
        status: shouldGetStatusFromResult(action.form.metadata.status)
          ? getOrdersBasketFormReadyStatus(action.form.result)
          : action.form.metadata.status,
        wrapper: action.form.metadata.wrapper,
        productType: action.form.metadata.productType,
        productSubType: action.form.metadata.productSubType,
        quoteAcknowledgement: action.form.metadata.quoteAcknowledgement,
        quoteResponse: action.form.metadata.quoteResponse,
        tradeResponse: action.form.metadata.tradeResponse,
      }));
    case 'ORDERS_BASKET_PERSISTED_FORM_FETCH_FAILED':
      return patchKey(state, action.formId, () => ({
        status: 'FETCH_ERROR' as const,
      }));
    case 'ORDERS_BASKET_IMPORT_REQUESTED':
      return addKey(state, action.temporaryNegotiationId, {
        negotiationId: action.temporaryNegotiationId,
        isSelected: false,
        version: -1,
        status: 'INITIALIZING' as const,
      } as Partial<OrdersBasketForm>);
    case 'ORDERS_BASKET_IMPORT_RECEIVED':
      return renameKey(
        patchKey(state, action.temporaryNegotiationId, () => ({
          negotiationId: action.form.negotiationId,
          result: action.form,
          version: action.form.version,
          status: getOrdersBasketFormReadyStatus(action.form),
          wrapper: action.form.values.Wrapper as unknown as string,
          productType: action.form.values.ProductType as unknown as string,
          productSubType: action.form.values.ProductSubtype as unknown as string,
        })),
        action.temporaryNegotiationId,
        action.form.negotiationId,
      );
    case 'ORDERS_BASKET_IMPORT_FAILED':
    case 'ORDERS_BASKET_IMPORT_WRONG_PRODUCT_RECEIVED':
      return removeKey(state, action.temporaryNegotiationId);
    case 'ORDERS_BASKET_INIT_FORM_REQUESTED':
      return addKey(state, action.temporaryNegotiationId, {
        negotiationId: action.temporaryNegotiationId,
        isSelected: false,
        version: -1,
        status: 'INITIALIZING' as const,
        wrapper: action.request.wrapper,
        productType: action.request.productType,
        productSubType: action.request.productSubType,
      } as Partial<OrdersBasketForm>);
    case 'ORDERS_BASKET_INIT_FORM_FAILED':
      return patchKey(state, action.temporaryNegotiationId, () => ({ status: 'INITIALIZATION_ERROR' as const }));
    case 'BOOKING_REQUESTED':
      return patchKey(state, action.formId, () => ({
        status: 'BOOKING_REQUESTED' as const,
        quoteResponse: undefined,
        quoteAcknowledgement: undefined,
        tradeAuthorization: undefined,
      }));
    case 'BOOKING_ACKED':
      return patchKey(state, action.formId, previousState => ({
        status: 'BOOKING_ACKED' as const,
        version: action.newVersion ?? previousState.version,
      }));
    case 'ORDERS_BASKET_ALL_BOOKING_RESULTS_RECEIVED':
      return patchKey(state, action.formId, () => ({
        status: 'EXECUTED' as const,
      }));
    default:
      return state;
  }
}
